import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, Lock, Loader2, ShoppingCart, Volume2, VolumeX, AlertTriangle } from 'lucide-react';
import { LanguageSelector } from '../components/LanguageSelector';
import { MyErotikaLogo } from '../components/MyErotikaLogo';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SelectCharacterPage() {
  const navigate = useNavigate();
  const { gender } = useParams();
  const { user, token, logout, isCharacterUnlocked, refreshUser, isGuest } = useAuth();
  const { t, language } = useLanguage();
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [playingVoice, setPlayingVoice] = useState(null);
  const [loadingVoice, setLoadingVoice] = useState(null);
  const audioRef = useRef(null);
  const [guestWarningOpen, setGuestWarningOpen] = useState(false);
  const [pendingPurchaseChar, setPendingPurchaseChar] = useState(null);

  // Age verification
  const [ageVerified, setAgeVerified] = useState(() => {
    return localStorage.getItem('age_verified') === 'true';
  });
  const [ageBlocked, setAgeBlocked] = useState(false);

  useEffect(() => {
    fetchCharacters();
  }, [gender, language, token]);

  const fetchCharacters = async () => {
    try {
      const response = await axios.get(`${API}/characters/${gender}?language=${language}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCharacters(response.data);
    } catch (error) {
      console.error('Error fetching characters:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (character) => {
    if (character.coming_soon) return;
    const isUnlocked = character.is_unlocked || character.is_free;
    if (isUnlocked) {
      handleSelect(character);
    } else {
      handlePurchase(character);
    }
  };

  const handleSelect = (character) => {
    if (!character.is_unlocked && !character.is_free) {
      // Show purchase option
      return;
    }
    navigate(`/select-level/${gender}/${character.id}`);
  };

  const handlePurchase = async (character) => {
    // If guest, show warning modal first
    if (isGuest) {
      setPendingPurchaseChar(character);
      setGuestWarningOpen(true);
      return;
    }
    executePurchase(character);
  };

  const executePurchase = async (character) => {
    setPurchasing(character.id);
    try {
      const returnPath = `/select-level/${gender}/${character.id}`;
      localStorage.setItem('erotika_payment_return', returnPath);

      const response = await axios.post(`${API}/purchase/checkout`, {
        item_type: 'character',
        item_id: character.id,
        origin_url: window.location.origin,
        return_to: returnPath
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'achat');
    } finally {
      setPurchasing(null);
    }
  };

  const confirmGuestPurchase = () => {
    setGuestWarningOpen(false);
    if (pendingPurchaseChar) {
      executePurchase(pendingPurchaseChar);
      setPendingPurchaseChar(null);
    }
  };

  const genderLabels = {
    woman: { fr: 'Femmes', en: 'Women', es: 'Mujeres', de: 'Frauen', pt: 'Mulheres' },
    man: { fr: 'Hommes', en: 'Men', es: 'Hombres', de: 'Männer', pt: 'Homens' },
    couple: { fr: 'Couples', en: 'Couples', es: 'Parejas', de: 'Paare', pt: 'Casais' }
  };

  // Play character voice preview
  const playVoicePreview = async (character) => {
    // Stop current audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    
    // If clicking the same character that's playing, just stop
    if (playingVoice === character.id) {
      setPlayingVoice(null);
      return;
    }
    
    setLoadingVoice(character.id);
    
    try {
      // Generate a greeting phrase for the character
      const greetings = {
        fr: `Bonjour, je suis ${character.name}. Ravie de faire ta connaissance...`,
        en: `Hello, I'm ${character.name}. Nice to meet you...`
      };
      
      const response = await axios.post(`${API}/tts/preview`, {
        text: greetings[language],
        character_id: character.id,
        language: language
      });
      
      if (response.data.audio_url) {
        const audio = new Audio(response.data.audio_url);
        audioRef.current = audio;
        
        audio.onended = () => {
          setPlayingVoice(null);
          audioRef.current = null;
        };
        
        audio.onerror = () => {
          setPlayingVoice(null);
          audioRef.current = null;
          toast.error(language === 'fr' ? 'Erreur de lecture audio' : 'Audio playback error');
        };
        
        await audio.play();
        setPlayingVoice(character.id);
      }
    } catch (error) {
      console.error('Voice preview error:', error);
      toast.error(language === 'fr' ? 'Voix indisponible' : 'Voice unavailable');
    } finally {
      setLoadingVoice(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-velvet-bg flex items-center justify-center">
        <Loader2 className="animate-spin text-purple-main" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-velvet-bg">
      {/* Age Verification Modal */}
      <AlertDialog open={!ageVerified && !ageBlocked}>
        <AlertDialogContent className="bg-velvet-bg border border-purple-main/30 max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-velvet_text-primary text-2xl font-heading text-center">
              {language === 'fr' ? 'Vérification de l\'âge' : language === 'es' ? 'Verificación de edad' : language === 'de' ? 'Altersüberprüfung' : language === 'pt' ? 'Verificação de idade' : 'Age Verification'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-velvet_text-secondary text-center text-base mt-4">
              {language === 'fr' ? 'Ce contenu est réservé aux personnes majeures. Avez-vous 18 ans ou plus ?' : language === 'es' ? 'Este contenido es solo para adultos. ¿Tiene 18 años o más?' : language === 'de' ? 'Dieser Inhalt ist nur für Erwachsene. Sind Sie 18 Jahre oder älter?' : language === 'pt' ? 'Este conteúdo é apenas para adultos. Tem 18 anos ou mais?' : 'This content is for adults only. Are you 18 years or older?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex gap-4 sm:justify-center mt-6">
            <AlertDialogCancel
              onClick={() => setAgeBlocked(true)}
              className="bg-transparent border border-red-500/50 text-red-400 hover:bg-red-500/10 rounded-full px-8"
              data-testid="age-no-button"
            >
              {({fr: 'Non', en: 'No', es: 'No', de: 'Nein', pt: 'Não'})[language] || 'No'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                localStorage.setItem('age_verified', 'true');
                setAgeVerified(true);
              }}
              className="bg-purple-main hover:bg-purple-dark text-white rounded-full px-8"
              data-testid="age-yes-button"
            >
              {language === 'fr' ? 'Oui, j\'ai 18 ans ou plus' : language === 'es' ? 'Sí, tengo 18 o más' : language === 'de' ? 'Ja, ich bin 18+' : language === 'pt' ? 'Sim, tenho 18+' : 'Yes, I am 18+'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Age Blocked Screen */}
      {ageBlocked && (
        <div className="fixed inset-0 z-[100] bg-velvet-bg flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="text-red-400" size={40} />
            </div>
            <h1 className="font-heading text-3xl text-velvet_text-primary mb-4">
              {language === 'fr' ? 'Accès refusé' : language === 'es' ? 'Acceso denegado' : language === 'de' ? 'Zugang verweigert' : language === 'pt' ? 'Acesso negado' : 'Access Denied'}
            </h1>
            <p className="text-velvet_text-secondary mb-8">
              {language === 'fr' ? 'Vous devez avoir 18 ans ou plus pour accéder à ce contenu.' : language === 'es' ? 'Debe tener 18 años o más para acceder a este contenido.' : language === 'de' ? 'Sie müssen 18 Jahre oder älter sein, um auf diesen Inhalt zugreifen zu können.' : language === 'pt' ? 'Deve ter 18 anos ou mais para aceder a este conteúdo.' : 'You must be 18 years or older to access this content.'}
            </p>
            <Button
              onClick={() => navigate('/')}
              className="bg-purple-main/20 text-purple-light hover:bg-purple-main/30 rounded-full px-8"
              data-testid="age-blocked-back"
            >
              {({fr: 'Retour à l\'accueil', en: 'Back to home', es: 'Volver al inicio', de: 'Zurück zur Startseite', pt: 'Voltar ao início'})[language] || 'Back to home'}
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/select-gender')}
            className="flex items-center gap-2 text-velvet_text-secondary hover:text-velvet_text-accent transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            <MyErotikaLogo size="sm" />
          </button>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <div className="flex items-center gap-3">
              <span className="text-velvet_text-secondary text-sm">
                {user?.username}
              </span>
              <Button 
                variant="ghost" 
                onClick={logout}
                className="text-velvet_text-secondary hover:text-velvet_text-primary text-sm"
                data-testid="logout-button"
              >
                {t('logout')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-4">
            <span className="text-velvet_text-accent text-sm font-medium tracking-wider uppercase">
              {({fr: 'Étape 2 sur 4', en: 'Step 2 of 4', es: 'Paso 2 de 4', de: 'Schritt 2 von 4', pt: 'Passo 2 de 4'})[language] || 'Step 2 of 4'}
            </span>
          </div>
          <h1 className="font-heading text-4xl lg:text-5xl text-center gradient-text mb-4 animate-slideUp">
            {({fr: 'Choisissez votre partenaire', en: 'Choose your partner', es: 'Elige a tu pareja', de: 'Wähle deinen Partner', pt: 'Escolhe o teu parceiro'})[language] || 'Choose your partner'}
          </h1>
          <p className="text-velvet_text-secondary text-center mb-4">
            {genderLabels[gender]?.[language] || gender}
          </p>
          <p className="text-velvet_text-secondary text-center mb-16 max-w-xl mx-auto text-sm">
            {({fr: 'Sélectionnez la personnalité qui vous attire le plus', en: 'Select the personality that attracts you the most', es: 'Selecciona la personalidad que más te atraiga', de: 'Wähle die Persönlichkeit, die dich am meisten anzieht', pt: 'Seleciona a personalidade que mais te atrai'})[language] || 'Select the personality that attracts you the most'}
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {characters.map((character, index) => {
              const isUnlocked = character.is_unlocked || character.is_free;
              const isComingSoon = character.coming_soon === true;
              
              return (
                <div
                  key={character.id}
                  onClick={() => handleCardClick(character)}
                  className={`group relative overflow-hidden rounded-2xl glass ${isComingSoon ? 'opacity-90 cursor-default' : 'card-hover cursor-pointer'} animate-slideUp stagger-${index + 1}`}
                  data-testid={`character-card-${character.id}`}
                >
                  {/* Image */}
                  <div className="aspect-[3/4] overflow-hidden relative">
                    <img 
                      src={character.image}
                      alt={character.name}
                      className={`w-full h-full object-cover object-top transition-transform duration-700 ${isComingSoon ? 'filter brightness-[0.75] grayscale-[0.1]' : ''} ${!isComingSoon ? 'group-hover:scale-110' : ''} ${!isUnlocked && !isComingSoon ? 'filter brightness-[0.82]' : ''}`}
                    />
                    
                    {/* Lock overlay for locked characters */}
                    {!isUnlocked && !isComingSoon && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="glass rounded-full p-4">
                          <Lock className="text-purple-light" size={32} />
                        </div>
                      </div>
                    )}
                    
                    {/* Badge */}
                    {isComingSoon ? (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="px-6 py-3 text-3xl font-bold text-purple-light italic tracking-wider drop-shadow-lg" style={{textShadow: '0 0 20px rgba(168,85,247,0.5)'}}>
                          {language === 'fr' ? 'Bientôt' : language === 'es' ? 'Próximamente' : language === 'de' ? 'Demnächst' : language === 'pt' ? 'Em breve' : 'Soon'}
                        </span>
                      </div>
                    ) : (
                    <div className="absolute top-4 right-4">
                      {character.is_free ? (
                        <span className="px-4 py-1.5 bg-green-600 rounded-full text-white text-sm font-bold shadow-lg">
                          {language === 'fr' ? 'Gratuit' : language === 'es' ? 'Gratis' : language === 'de' ? 'Kostenlos' : language === 'pt' ? 'Gratuito' : 'Free'}
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-black/80 border border-purple-main/50 rounded-full text-purple-light text-xs font-medium backdrop-blur-sm">
                          <span className="line-through opacity-60 mr-1">1,99 €</span> 0,99 €
                        </span>
                      )}
                    </div>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-heading text-2xl text-velvet_text-primary">
                        {character.name}
                      </h3>
                      {/* Voice preview button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          playVoicePreview(character);
                        }}
                        disabled={loadingVoice === character.id}
                        className={`p-2 rounded-full transition-all ${
                          playingVoice === character.id 
                            ? 'bg-purple-main text-white' 
                            : 'bg-purple-main/20 text-purple-light hover:bg-purple-main/40'
                        }`}
                        data-testid={`voice-preview-${character.id}`}
                        title={language === 'fr' ? 'Écouter la voix' : 'Listen to voice'}
                      >
                        {loadingVoice === character.id ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : playingVoice === character.id ? (
                          <VolumeX size={16} />
                        ) : (
                          <Volume2 size={16} />
                        )}
                      </button>
                    </div>
                    <p className="text-velvet_text-accent text-sm mb-3">
                      {character.personality}
                    </p>
                    <p className="text-velvet_text-secondary text-sm mb-4">
                      {character.description}
                    </p>
                    
                    {/* Action button */}
                    {isComingSoon ? (
                      <Button
                        disabled
                        className="w-full bg-purple-main/20 text-purple-light/50 rounded-xl cursor-not-allowed italic"
                        data-testid={`coming-soon-${character.id}`}
                      >
                        {language === 'fr' ? 'Prochainement disponible' : language === 'es' ? 'Próximamente disponible' : language === 'de' ? 'Demnächst verfügbar' : language === 'pt' ? 'Disponível em breve' : 'Coming soon'}
                      </Button>
                    ) : isUnlocked ? (
                      <Button
                        onClick={() => handleSelect(character)}
                        className="w-full bg-purple-main hover:bg-purple-dark text-white rounded-xl"
                        data-testid={`select-${character.id}`}
                      >
                        {t('select')}
                      </Button>
                    ) : (
                      <Button
                        onClick={() => handlePurchase(character)}
                        disabled={purchasing === character.id}
                        className="w-full bg-gradient-to-r from-purple-main to-fuchsia-main hover:opacity-90 text-white rounded-xl"
                        data-testid={`buy-${character.id}`}
                      >
                        {purchasing === character.id ? (
                          <Loader2 className="animate-spin" size={18} />
                        ) : (
                          <>
                            <ShoppingCart size={16} className="mr-2" />
                            {language === 'fr' ? (<>Débloquer <span className="line-through opacity-60 mr-1">1,99 €</span> 0,99 €</>) : language === 'es' ? (<>Desbloquear <span className="line-through opacity-60 mr-1">1,99 €</span> 0,99 €</>) : language === 'de' ? (<>Freischalten <span className="line-through opacity-60 mr-1">1,99 €</span> 0,99 €</>) : language === 'pt' ? (<>Desbloquear <span className="line-through opacity-60 mr-1">1,99 €</span> 0,99 €</>) : (<>Unlock <span className="line-through opacity-60 mr-1">€1.99</span> €0.99</>)}
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
            
            {/* Coming Soon Card */}
            <div
              className={`group relative overflow-hidden rounded-2xl glass animate-slideUp stagger-${characters.length + 1}`}
              data-testid="character-card-coming-soon"
            >
              {/* Placeholder Image */}
              <div className="aspect-[3/4] overflow-hidden relative bg-purple-main/5 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-24 h-24 mx-auto mb-4 rounded-full border-2 border-dashed border-purple-main/50 flex items-center justify-center">
                    <span className="text-4xl text-purple-light/70">?</span>
                  </div>
                </div>
                
                
                {/* Badge */}
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 bg-purple-main/10 border border-purple-main/20 rounded-full text-purple-light/70 text-xs font-medium italic">
                    {({fr: 'Bientôt', en: 'Soon', es: 'Próximamente', de: 'Demnächst', pt: 'Em breve'})[language] || 'Soon'}
                  </span>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-6">
                <h3 className="font-heading text-2xl text-velvet_text-primary/50 mb-1 italic">
                  {({fr: 'Bientôt...', en: 'Coming soon...', es: 'Próximamente...', de: 'Demnächst...', pt: 'Em breve...'})[language] || 'Coming soon...'}
                </h3>
                <p className="text-velvet_text-accent/50 text-sm mb-3 italic">
                  {({fr: 'Nouveau personnage', en: 'New character', es: 'Nuevo personaje', de: 'Neue Persönlichkeit', pt: 'Novo personagem'})[language] || 'New character'}
                </p>
                <p className="text-velvet_text-secondary/50 text-sm mb-4 italic">
                  {language === 'fr' 
                    ? 'De nouvelles personnalités seront bientôt disponibles pour enrichir votre expérience.'
                    : 'New personalities will soon be available to enrich your experience.'
                  }
                </p>
                
                {/* Disabled button */}
                <Button
                  disabled
                  className="w-full bg-purple-main/20 text-purple-light/50 rounded-xl cursor-not-allowed"
                >
                  {({fr: 'Prochainement', en: 'Coming soon', es: 'Próximamente', de: 'Demnächst', pt: 'Em breve'})[language] || 'Coming soon'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Guest Purchase Warning Modal */}
      <AlertDialog open={guestWarningOpen} onOpenChange={setGuestWarningOpen}>
        <AlertDialogContent className="bg-velvet-bg border border-purple-main/30">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-velvet_text-primary flex items-center gap-2">
              <AlertTriangle className="text-amber-400" size={20} />
              {({fr: 'Attention - Mode Invité', en: 'Warning - Guest Mode', es: 'Atención - Modo Invitado', de: 'Achtung - Gastmodus', pt: 'Atenção - Modo Convidado'})[language] || 'Warning - Guest Mode'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-velvet_text-secondary">
              {language === 'fr' 
                ? 'Vous êtes en mode invité. Vos achats ne seront PAS sauvegardés si vous quittez l\'application sans créer de compte. Créez un compte après votre achat pour conserver vos achats.'
                : 'You are in guest mode. Your purchases will NOT be saved if you leave the app without creating an account. Create an account after your purchase to keep your purchases.'
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="border-purple-main/50 text-velvet_text-secondary hover:bg-purple-main/10"
              data-testid="guest-warning-cancel"
            >
              {({fr: 'Annuler', en: 'Cancel', es: 'Cancelar', de: 'Abbrechen', pt: 'Cancelar'})[language] || 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmGuestPurchase}
              className="bg-gradient-to-r from-purple-main to-fuchsia-main text-white hover:opacity-90"
              data-testid="guest-warning-confirm"
            >
              {({fr: 'Continuer l\'achat', en: 'Continue purchase', es: 'Continuar compra', de: 'Kauf fortsetzen', pt: 'Continuar compra'})[language] || 'Continue purchase'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
